import { useCallback, useEffect, useState } from 'react';
import {
  APP_VERSION,
  fetchRemoteVersion,
  isRemoteVersionNewer,
  reloadForAppUpdate,
} from '../utils/appCache';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

export function useAppUpdateCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState<string | null>(null);

  const checkForUpdate = useCallback(async () => {
    const remote = await fetchRemoteVersion();
    if (isRemoteVersionNewer(remote)) {
      setUpdateAvailable(true);
      setRemoteVersion(remote?.version ?? null);
      return true;
    }
    setUpdateAvailable(false);
    return false;
  }, []);

  useEffect(() => {
    void checkForUpdate();

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    const intervalId = window.setInterval(() => void checkForUpdate(), CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(intervalId);
    };
  }, [checkForUpdate]);

  return {
    updateAvailable,
    currentVersion: APP_VERSION,
    remoteVersion,
    checkForUpdate,
    applyUpdate: reloadForAppUpdate,
  };
}
